/**
 * `TopluHacizHazirlaView.sihirbazaYazmayiDene` deneme döngüsünün saf karar
 * fonksiyonu (B09, DEV-2603).
 *
 * Ayrı dosyada tutulmasının nedeni test edilebilirlik: `TopluHacizHazirlaView.vue`
 * bu projede `vue-jest` + `vue-template-es2015-compiler` zinciriyle derleniyor ve
 * o derleyici (Node 24 altında) şablonu ayrıştırırken patlıyor — `.vue` dosyasını
 * doğrudan import eden hiçbir test yok, bu da o yolu birim testi için kapatıyor.
 * Karar mantığı Vue'ya hiç dokunmadığından (yalnız sayaç + iki boolean) düz bir
 * `.ts` modülüne taşınması davranışı değiştirmeden testi mümkün kılıyor.
 */

export type DenemeSonrasiEylem = "tekrar-dene" | "vazgec" | "uyap-bekle";

/**
 * `sihirbazaYazmayiDene` her başarısız denemeden SONRA (sayaç zaten artırılmış
 * haliyle çağrılır) ne yapılacağına karar verir — saf fonksiyon, Vue'ya
 * ($nextTick, $refs, $watch) hiç dokunmaz; birim testi doğrudan çağırır.
 *
 * Eskiden azami deneme tükenince `sihirbazaYazmayiDene` sessizce hiçbir şey
 * yapmadan çıkıyordu: üstteki bilgi şeridi (`v-if="kopruGirdisi"`) "önceden
 * seçildi" demeye SONSUZA dek devam ediyordu, sihirbaz ağacı hiç kurulamamış
 * olsa bile. Bir sonraki düzeltme "vazgec" sonucunu ekledi: çağıran taraf
 * bunu görünce `izleyicileriKur`'daki `kopruDustu` yoluyla AYNI üç atamayı
 * yapar (`kopruGirdisi = null`, `bekleyenOneriId = null`, `kopruDustu = true`)
 * ve sarı "Öneriden gelen ön seçim kaldırıldı" uyarısı devreye girer.
 *
 * B09 REGRESYONU (DEV-2603): o düzeltme deneme tükenmesini UYAP bağlı olsun ya
 * da olmasın HER ZAMAN "vazgec" saydı. Ama şablonda sihirbaz
 * `hazir && $store.getters.isUyapConnected` koşuluyla mount edilir — UYAP
 * bağlı DEĞİLKEN `$refs.wizard` hiç var olmaz, dolayısıyla sayfa UYAP
 * bağlanmadan açıldığında 30 denemenin TAMAMI (gerçek bir düşme olmadığı
 * halde) başarısız olur ve `kopruDustu = true` kalıcı olarak yazılır. Bu
 * bayrak bir daha `false` olmadığı için `onUyapBaglantisiDegisti`
 * izleyicisindeki `bagli && !kopruDustu` koşulu artık asla sağlanmaz — UYAP
 * sonradan bağlansa bile ön seçim BİR DAHA hiç yeniden denenmez.
 *
 * Düzeltme: `uyapBagli` parametresi eklendi. Tükenme anında UYAP bağlı
 * DEĞİLSE bu gerçek bir düşme değildir — "uyap-bekle" döner; çağıran taraf
 * `kopruGirdisi`yi KORUR ve `kopruDustu`yu `false` bırakır, böylece UYAP
 * bağlanınca izleyici sayaç sıfırlanmış halde yeniden dener. Tükenme anında
 * UYAP zaten BAĞLIYSA (sihirbaz mount olabilecekken yine de kurulamadıysa)
 * bu gerçek bir düşmedir — "vazgec" eski davranışıyla döner.
 */
export function denemeSonrasiEylem(
    yeniDenemeSayisi: number,
    azamiDeneme: number,
    uyapBagli: boolean
): DenemeSonrasiEylem {
    if (yeniDenemeSayisi < azamiDeneme) return "tekrar-dene";
    return uyapBagli ? "vazgec" : "uyap-bekle";
}

/** `kopruOtomatikGonderimKarari` sonucu — çağıran taraf bunu doğrudan alanlara yazar. */
export interface KopruOtomatikGonderimKarari {
    /**
     * true ise sihirbaza `otomatikTalepGonder = false` YAZILMALI — yalnız bu
     * sayfa örneğinin belleğinde; kalıcı bir tercihe DOKUNULMAZ (öyle bir
     * tercih zaten yok, anahtar sabit `false` ile başlayan düz bir alan).
     */
    zorlaKapat: boolean;
    /** Bilgi şeridinde "öneriden gelindiği için kapatıldı" cümlesi gösterilsin mi. */
    kapatildiBilgisiGoster: boolean;
    /** Bilgi şeridinde mevcut "açık, daraltma yetişmez" uyarısı (otomatikGonderimAcik) gösterilsin mi. */
    acikUyarisiGoster: boolean;
}

/**
 * Köprüden gelinen sayfa örneğinde "Sorgulama bittikten sonra talepleri
 * otomatik gönder" anahtarının durumuna karar verir (B11, DEV-3345).
 *
 * SORUN: Köprü sihirbaza TEK bir takip yazar; öneriye konu borçlu/varlık
 * daraltması ise "Talepleri Hazırla" bittikten SONRA, sihirbazın `durum`
 * alanı izlenerek uygulanır (bkz. `TopluHacizHazirlaView.borcluSecimiDaralt`).
 * Otomatik gönderim açıkken sihirbaz `durum = hazir` olur olmaz SENKRON olarak
 * göndermeyi başlatıyor; daraltma izleyicisi ise Vue'nun watcher kuyruğunda
 * bir adım GERİDE kaldığı için yetişemiyor ve öneriye konu olmayan
 * borçlu/varlıklara da talep gidebiliyor (bkz. docs/sorgu-snapshot-gecmis.md, 10 Eylül DEV-3345/B11).
 *
 * KARAR: Ek bir onay diyaloğu eklemek yerine — köprüden gelindiğinde bu anahtar
 * SAYFA ÖRNEĞİ için kapalı başlar. Kullanıcı anahtarı elle açarsa müdahale
 * EDİLMEZ (bu ürün kararı); mevcut "açık, daraltma yetişmez" uyarısı o yolu
 * zaten haber veriyor. `zorlaKapat` bu yüzden yalnız İLK kez (henüz hiç
 * zorla kapatılmamışken) true döner — aksi halde kullanıcının elle açtığı
 * anahtar, sihirbaz her yeniden değerlendirildiğinde sessizce geri kapanırdı.
 *
 * @param kopruVarMi Bu sayfa örneği köprüyle mi açıldı (`kopruGirdisi !== null`).
 * @param otomatikTalepGonder Sihirbazın anahtarının ŞU ANKİ değeri.
 * @param zatenZorlaKapatildiMi Bu sayfa örneğinde anahtar daha önce zorla kapatıldı mı.
 */
export function kopruOtomatikGonderimKarari(
    kopruVarMi: boolean,
    otomatikTalepGonder: boolean,
    zatenZorlaKapatildiMi: boolean
): KopruOtomatikGonderimKarari {
    if (!kopruVarMi) {
        // Köprü yok: bu özellik hiç devrede değil, mevcut davranış (yalnız
        // "açık" uyarısı) birebir korunur.
        return {zorlaKapat: false, kapatildiBilgisiGoster: false, acikUyarisiGoster: otomatikTalepGonder};
    }

    if (!zatenZorlaKapatildiMi) {
        return {zorlaKapat: true, kapatildiBilgisiGoster: true, acikUyarisiGoster: false};
    }

    // Daha önce zorla kapatıldı: kullanıcı anahtara ne yaptıysa ona saygı duyulur.
    return {
        zorlaKapat: false,
        kapatildiBilgisiGoster: !otomatikTalepGonder,
        acikUyarisiGoster: otomatikTalepGonder
    };
}
